import React from 'react';
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { MultiSelect } from '@web/atoms';
import { Footer } from '../Footer';
import { Question } from '../Question';
import { StyledForm } from '../StyledForm';
import { FormFieldsContainer } from '../FormFieldsContainer';
import { ApiResult } from '../../api';
import { useReporter } from '../../reporter';
import { useSessionCustomer } from '../../customer';
import { onboardingSubmitted } from '../events';
import { useGetBreeds } from '../../breeds';
import { schema } from './schema';

export { schema } from './schema';

export const BreedForm = ({
  breedQuestionPrompt,
  defaultValues,
  goBack,
  index,
  labelBreed,
  onSubmit,
  ctaButtonText,
}) => {
  const getBreeds = useGetBreeds();
  const {
    handleSubmit,
    control,
    formState: { isValid, isSubmitting, isSubmitted },
    reset,
    getValues,
  } = useForm({
    mode: 'onChange',
    defaultValues: defaultValues,
    resolver: yupResolver(schema),
  });
  const reporter = useReporter();
  const { customer } = useSessionCustomer();

  const submitForm = async data => {
    reporter.tag(onboardingSubmitted(index, 'dog_breed', customer));
    const result = await onSubmit(data);
    ApiResult.ifError(result, () =>
      setTimeout(() =>
        reset(getValues(), {
          keepIsSubmitted: false,
        }),
      ),
    );
  };

  return (
    <StyledForm onSubmit={handleSubmit(submitForm)}>
      <FormFieldsContainer $maxWidth={391}>
        <Question prompt={breedQuestionPrompt}>
          <Controller
            name="breeds"
            control={control}
            render={({ field: { ref, ...field } }) => (
              <MultiSelect
                {...field}
                searchLabel={labelBreed}
                values={getBreeds}
                selectedValues={field.value}
                max={{ count: 2, message: 'Maximum of two breeds allowed' }}
              />
            )}
          />
        </Question>
      </FormFieldsContainer>

      <Footer
        mobileCTAText={ctaButtonText?.mobileCTA}
        desktopCTAText={ctaButtonText?.desktopCTA}
        onBackButtonClick={goBack}
        isForwardButtonDisabled={!isValid || isSubmitting || isSubmitted}
        isBackButtonDisabled={isSubmitting || isSubmitted}
        isForwardButtonLoading={isSubmitting || isSubmitted}
      />
    </StyledForm>
  );
};

BreedForm.displayName = 'BreedForm';

BreedForm.propTypes = {
  breedQuestionPrompt: PropTypes.string.isRequired,
  defaultValues: PropTypes.shape({ breeds: PropTypes.array }).isRequired,
  goBack: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  labelBreed: PropTypes.string.isRequired,
  onSubmit: PropTypes.func.isRequired,
  ctaButtonText: PropTypes.object,
};
